import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from 'src/common/utils/api';
import { invalidateDataUploadFiles } from 'src/data-capture/hooks/file-uploads/invalidateDataUploadFiles';
import { UploadedFile } from 'src/data-capture/hooks/file-uploads/types';

type UseFamilyFileActionsOptions = {
  familyId: string;
};

const ownerResource = (file: UploadedFile) => `clients/${file.clientId}/data-upload-files`;

export const useFamilyFileActions = ({ familyId }: UseFamilyFileActionsOptions) => {
  const queryClient = useQueryClient();
  const invalidate = (file: UploadedFile) =>
    invalidateDataUploadFiles(queryClient, { clientId: file.clientId, familyId });

  const {
    mutateAsync: deleteFileMutation,
    error: deleteError,
    reset: resetDeleteError,
    isPending: isDeleting,
  } = useMutation<unknown, Error, UploadedFile>({
    mutationFn: file => api.delete(`${ownerResource(file)}/${file.id}`),
    onSuccess: (_data, file) => invalidate(file),
  });

  const moveFile = async (file: UploadedFile, parentId: string | null | undefined) => {
    await api.put(`${ownerResource(file)}/${file.id}`, {
      parentId: parentId ?? null,
      isResolved: file.isResolved,
      isFiled: true,
      fileName: null,
      documentDate: file.documentDate ?? null,
    });
    await invalidate(file);
  };

  const renameFolder = async (file: UploadedFile, newName: string) => {
    await api.put(`${ownerResource(file)}/${file.id}`, {
      isResolved: file.isResolved,
      isFiled: false,
      parentId: file.parentId ?? null,
      fileName: newName,
    });
    await invalidate(file);
  };

  const updateDocumentDate = async (file: UploadedFile, documentDate: Date) => {
    await api.put(`${ownerResource(file)}/${file.id}`, {
      documentDate: documentDate.toISOString().split('T')[0],
      isResolved: file.isResolved,
      parentId: file.parentId ?? null,
      isFiled: false,
    });
    await invalidate(file);
  };

  const deleteFile = async (file: UploadedFile) => {
    await deleteFileMutation(file);
  };

  return {
    deleteFile,
    deleteError,
    resetDeleteError,
    isDeleting,
    moveFile,
    renameFolder,
    updateDocumentDate,
  };
};
